/**
 * 파일 첨부 컴포넌트
 * 파일 업로드 및 관리
 */

import React, { useRef, useState } from 'react';
import { 
  Upload, 
  File, 
  X, 
  Image, 
  FileText,
  Download,
  Eye,
  AlertCircle 
} from 'lucide-react';
import { Button } from '../UI';

function FileAttachment({ attachments = [], onAttach, onRemove, maxFileSize = 10485760, maxTotalSize = 52428800 }) {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  
  // 파일 크기 포맷
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };
  
  // 파일 아이콘 선택
  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return <Image size={20} />;
    if (type.includes('pdf')) return <FileText size={20} />;
    return <File size={20} />;
  };
  
  // 파일 유효성 검사
  const validateFiles = (files) => {
    setError('');
    
    // 파일 크기 체크
    for (const file of files) {
      if (file.size > maxFileSize) {
        setError(`${file.name}이(가) 최대 크기(10MB)를 초과합니다.`);
        return false;
      }
    }
    
    // 전체 크기 체크
    const currentSize = attachments.reduce((sum, att) => sum + att.size, 0);
    const newSize = Array.from(files).reduce((sum, file) => sum + file.size, 0);
    
    if (currentSize + newSize > maxTotalSize) {
      setError('전체 첨부 파일 크기가 50MB를 초과합니다.');
      return false;
    }
    
    return true;
  };
  
  // 파일 선택 처리
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    if (validateFiles(files)) {
      const newAttachments = files.map(file => ({
        id: `${Date.now()}-${Math.random()}`,
        name: file.name,
        type: file.type,
        size: file.size,
        file: file, // 실제 File 객체는 업로드를 위해 임시 보관
        uploadedAt: new Date()
      }));
      
      onAttach(newAttachments);
    }
    
    // 입력 초기화
    e.target.value = '';
  };
  
  // 드래그 이벤트 처리
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  
  // 드롭 처리
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    
    if (validateFiles(files)) {
      const newAttachments = files.map(file => ({
        id: `${Date.now()}-${Math.random()}`,
        name: file.name,
        type: file.type,
        size: file.size,
        file: file, // 실제 File 객체는 업로드를 위해 임시 보관
        uploadedAt: new Date()
      }));
      
      onAttach(newAttachments);
    }
  };
  
  // 파일 보기 (새 탭에서 열기)
  const handleView = (attachment) => {
    if (attachment.url) {
      // 이미 업로드된 파일 - 그냥 URL로 열기
      window.open(attachment.url, '_blank', 'noopener,noreferrer');
    } else if (attachment.file) {
      // 새로 추가된 파일 (아직 업로드 안됨)
      const url = URL.createObjectURL(attachment.file);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  };
  
  // 파일 다운로드
  const handleDownload = (attachment) => {
    if (attachment.url) {
      // 이미 업로드된 파일
      const link = document.createElement('a');
      link.href = attachment.url;
      link.download = attachment.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (attachment.file) {
      // 새로 추가된 파일
      const url = URL.createObjectURL(attachment.file);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };
  
  // 전체 크기 계산
  const totalSize = attachments.reduce((sum, att) => sum + att.size, 0);
  const sizePercentage = (totalSize / maxTotalSize) * 100;
  
  return (
    <div className="file-attachment">
      {/* 드래그 앤 드롭 영역 */}
      <div
        className={`drop-zone ${dragActive ? 'active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload size={32} />
        <p>파일을 드래그하거나 클릭하여 선택</p>
        <p className="drop-zone-hint">
          최대 파일 크기: 10MB | 전체 최대: 50MB
        </p>
        
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          accept="image/*,.pdf,.doc,.docx,.txt,.zip"
        />
      </div>
      
      {/* 에러 메시지 */}
      {error && (
        <div className="attachment-error">
          <AlertCircle size={16} />
          {error}
        </div>
      )}
      
      {/* 첨부 파일 목록 */}
      {attachments.length > 0 && (
        <div className="attachment-list">
          <div className="attachment-header">
            <span>첨부 파일 ({attachments.length}개)</span>
            <span>{formatFileSize(totalSize)} / 50MB</span>
          </div>
          
          {/* 용량 표시 바 */}
          <div className="attachment-size-bar">
            <div 
              className="attachment-size-fill"
              style={{ 
                width: `${Math.min(sizePercentage, 100)}%`,
                backgroundColor: sizePercentage > 90 ? '#dc3545' : 
                               sizePercentage > 70 ? '#ffc107' : '#28a745'
              }}
            />
          </div>
          
          {/* 파일 목록 */}
          <div className="attachment-items">
            {attachments.map(attachment => (
              <div key={attachment.id} className="attachment-item">
                <div className="attachment-icon">
                  {getFileIcon(attachment.type)}
                </div>
                
                <div className="attachment-info">
                  <div className="attachment-name">
                    {attachment.name}
                  </div>
                  <div className="attachment-size">
                    {formatFileSize(attachment.size)}
                  </div>
                </div>
                
                <div className="attachment-actions">
                  <button
                    type="button"
                    className="attachment-action"
                    onClick={() => handleView(attachment)}
                    title="새 탭에서 보기"
                  >
                    <Eye size={16} />
                  </button>
                  {attachment.url && (
                    <button
                      type="button"
                      className="attachment-action"
                      onClick={() => handleDownload(attachment)}
                      title="다운로드"
                    >
                      <Download size={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    className="attachment-action danger"
                    onClick={() => onRemove(attachment.id)}
                    title="제거"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default FileAttachment;